/**
 * FAQ accordion toggle. Each [data-faq] item expands/collapses its answer panel
 * (animating max-height/opacity), swaps the +/- sign, syncs aria-expanded, and
 * hides closed panels from assistive tech. The first item renders open
 * server-side so the content is readable without JS.
 */

/**
 * Open or close a single FAQ item: animate its answer panel, swap the +/- sign,
 * and keep the toggle button's aria-expanded state in sync.
 *
 * @param {Element} item The [data-faq] wrapper element.
 * @param {boolean} open Whether the item should end up open.
 */
function setOpen(item, open) {
  const answer = item.querySelector('[data-faq-answer]');
  const sign = item.querySelector('[data-faq-sign]');
  const toggle = item.querySelector('[data-faq-toggle]');
  if (!answer) {
    return;
  }

  if (open) {
    answer.removeAttribute('aria-hidden');
    answer.inert = false;
    answer.style.maxHeight = `${answer.scrollHeight}px`;
    answer.style.opacity = '1';
    answer.style.marginTop = '12px';
    if (sign) {
      sign.textContent = '–';
    }
    item.setAttribute('data-open', '1');
    if (toggle) {
      toggle.setAttribute('aria-expanded', 'true');
    }
  } else {
    answer.setAttribute('aria-hidden', 'true');
    answer.inert = true;
    answer.style.maxHeight = '0px';
    answer.style.opacity = '0';
    answer.style.marginTop = '0px';
    if (sign) {
      sign.textContent = '+';
    }
    item.setAttribute('data-open', '0');
    if (toggle) {
      toggle.setAttribute('aria-expanded', 'false');
    }
  }
}

/**
 * Wire one FAQ item: match the server-rendered open answer to its true height so
 * the first collapse animates smoothly, and toggle the item on header click.
 *
 * @param {Element} item The [data-faq] wrapper element.
 */
function initItem(item) {
  const header = item.querySelector('[data-faq-toggle]');
  if (!header) {
    return;
  }

  // Match the server-rendered open item to its true content height.
  if (item.getAttribute('data-open') === '1') {
    const answer = item.querySelector('[data-faq-answer]');
    if (answer) {
      answer.style.maxHeight = `${answer.scrollHeight}px`;
    }
  }

  header.addEventListener('click', () => {
    setOpen(item, item.getAttribute('data-open') !== '1');
  });
}

/**
 * Initialise every FAQ accordion on the page and keep open answers sized
 * correctly on resize. Safe to call when no accordions are present.
 */
export function initFaq() {
  const items = document.querySelectorAll('[data-faq]');
  if (!items.length) {
    return;
  }

  items.forEach(initItem);

  window.addEventListener('resize', () => {
    items.forEach((item) => {
      if (item.getAttribute('data-open') === '1') {
        const answer = item.querySelector('[data-faq-answer]');
        if (answer) {
          answer.style.maxHeight = `${answer.scrollHeight}px`;
        }
      }
    });
  });
}
